/**
 * PATH: apps/quote/src/components/builder/paste-mapping-modal/PasteMappingModal.tsx
 * PURPOSE: Orchestrator modal for paste column mapping
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import { X, Check, RefreshCw, Save } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ColumnMapping, PasteMappingModalProps } from './types'
import { autoDetectMapping } from './types'
import { MappingRow } from './MappingRow'
import { PreviewTable } from './PreviewTable'

export default function PasteMappingModal({ isOpen, onClose, pasteData, availableFields, savedMapping, onApply, onSaveMapping, sectionName }: PasteMappingModalProps) {
  const [mappings, setMappings] = useState<ColumnMapping[]>([])
  const [useSavedMapping, setUseSavedMapping] = useState(false)

  const headers = pasteData[0] || []
  const sampleData = useMemo(() => headers.map((_, colIdx) => pasteData.slice(1, 5).map(row => row[colIdx] || '')), [pasteData, headers])

  useEffect(() => { if (!isOpen) return; setUseSavedMapping(!!savedMapping) }, [isOpen, savedMapping])
  useEffect(() => {
    if (!isOpen) return
    if (savedMapping && useSavedMapping) setMappings(savedMapping.mappings)
    else setMappings(autoDetectMapping(headers, availableFields))
  }, [isOpen, headers, availableFields, savedMapping, useSavedMapping])

  const usedFields = useMemo(() => new Set(mappings.map(m => m.targetField)), [mappings])
  const handleChangeMapping = useCallback((sourceIndex: number, targetField: string | null) => {
    setMappings(prev => { const filtered = prev.filter(m => m.sourceIndex !== sourceIndex); return targetField ? [...filtered, { sourceIndex, targetField }] : filtered })
  }, [])
  const handleReset = useCallback(() => { setMappings(autoDetectMapping(headers, availableFields)); setUseSavedMapping(false) }, [headers, availableFields])
  const handleApply = useCallback(() => { onApply(mappings); onClose() }, [mappings, onApply, onClose])
  const handleSaveMapping = useCallback(() => { onSaveMapping?.(mappings) }, [mappings, onSaveMapping])

  if (!isOpen) return null
  const rowCount = pasteData.length - 1

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[80vh] bg-white dark:bg-slate-900 rounded-lg shadow-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <div><h2 className="text-lg font-semibold text-slate-900 dark:text-white">Paste Import</h2><p className="text-sm text-slate-500">{rowCount} rows to import into {sectionName}</p></div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={handleReset} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"><RefreshCw className="w-3.5 h-3.5" />Auto-detect</button>
              {savedMapping && (<button onClick={() => setUseSavedMapping(true)} className={cn('flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border', useSavedMapping ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800')}>Use saved mapping</button>)}
            </div>
            {onSaveMapping && <button onClick={handleSaveMapping} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"><Save className="w-3.5 h-3.5" />Save mapping</button>}
          </div>
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400">Column Mapping</div>
            <div>{headers.map((header, idx) => (<MappingRow key={idx} sourceIndex={idx} sourceHeader={header} sampleValues={sampleData[idx]} targetField={mappings.find(m => m.sourceIndex === idx)?.targetField || null} availableFields={availableFields} onChangeTarget={(field) => handleChangeMapping(idx, field)} usedFields={usedFields} />))}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Preview (first 5 rows)</div>
            <PreviewTable data={pasteData} mappings={mappings} availableFields={availableFields} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
          <button onClick={handleApply} disabled={mappings.length === 0} className="flex items-center gap-1.5 px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"><Check className="w-4 h-4" />Import {rowCount} rows</button>
        </div>
      </div>
    </div>
  )
}
